<template>
  <HrPageLayout
    title="Attendance Clock"
    subtitle="Clock in and out, track your attendance"
    icon="⏰"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'Clock' }
    ]"
  >
    <div class="clock-content">
      <div v-if="loading" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p>Loading clock status...</p>
      </div>

      <div v-else-if="clockData">
        <!-- Current Time Display -->
        <div class="hr-clock-hero hr-mb-4">
          <h1 class="hr-clock-time">{{ currentTime }}</h1>
          <p class="hr-clock-date">{{ formatDateLong(currentDate) }}</p>
          <span class="hr-badge hr-badge-muted">
            <i class="bx bx-buildings"></i> {{ clockData.employee.department }}
          </span>
        </div>

        <!-- Clock Status & Action -->
        <div class="hr-content-grid hr-mb-4">
          <!-- Current Status Card -->
          <div class="hr-chart-card">
            <div class="hr-chart-body hr-text-center">
              <div class="hr-clock-status-icon" :class="clockData.clockStatus.isClockedIn ? 'hr-status-in' : 'hr-status-out'">
                <i :class="clockData.clockStatus.isClockedIn ? 'bx bx-check-circle' : 'bx bx-time-five'"></i>
              </div>
              <h4 class="hr-mb-3">
                <span v-if="clockData.clockStatus.isClockedIn" class="hr-text-success">
                  <i class="bx bx-circle"></i> Currently Clocked In
                </span>
                <span v-else class="hr-text-danger">
                  <i class="bx bx-circle"></i> Currently Clocked Out
                </span>
              </h4>
              <div v-if="clockData.clockStatus.checkInTime" class="hr-mb-3">
                <p class="hr-text-muted hr-mb-1">Check In Time</p>
                <h5 class="hr-text-success"><i class="bx bx-log-in"></i> {{ formatTime(clockData.clockStatus.checkInTime) }}</h5>
              </div>
              <div v-if="clockData.clockStatus.checkOutTime" class="hr-mb-3">
                <p class="hr-text-muted hr-mb-1">Check Out Time</p>
                <h5 class="hr-text-danger"><i class="bx bx-log-out"></i> {{ formatTime(clockData.clockStatus.checkOutTime) }}</h5>
              </div>
              <div v-if="clockData.clockStatus.isClockedIn && clockData.clockStatus.checkInTime" class="hr-mb-3">
                <p class="hr-text-muted hr-mb-1">Working Duration</p>
                <h3 class="hr-text-primary hr-mb-0">{{ workingDuration }}</h3>
              </div>
              <div v-if="!clockData.clockStatus.isClockedIn && clockData.clockStatus.totalHours > 0" class="hr-mb-3">
                <p class="hr-text-muted hr-mb-1">Total Hours Today</p>
                <h3 class="hr-text-primary hr-mb-0">{{ clockData.clockStatus.totalHours }}h</h3>
              </div>
              <div class="hr-clock-actions">
                <button v-if="clockData.clockStatus.isClockedIn" @click="openClockModal('OUT')"
                        class="hr-btn hr-btn-danger hr-btn-lg hr-w-100" :disabled="processing">
                  <i class="bx bx-log-out"></i> <span v-if="processing">Processing...</span><span v-else>Clock Out</span>
                </button>
                <button v-else @click="openClockModal('IN')"
                        class="hr-btn hr-btn-success hr-btn-lg hr-w-100" :disabled="processing">
                  <i class="bx bx-log-in"></i> <span v-if="processing">Processing...</span><span v-else>Clock In</span>
                </button>
                <button v-if="!clockData.clockStatus.isClockedIn" @click="openRemoteModal"
                        class="hr-btn hr-btn-outline-info hr-btn-lg hr-w-100 hr-mt-2" :disabled="processing">
                  <i class="bx bx-home-alt"></i> Mark Remote Attendance
                </button>
              </div>
            </div>
          </div>

          <!-- Work Schedule & Stats -->
          <div>
            <div class="hr-chart-card hr-mb-4">
              <div class="hr-chart-header">
                <h5 class="hr-chart-title"><i class="bx bx-time"></i> Today's Schedule</h5>
              </div>
              <div class="hr-chart-body">
                <div class="hr-schedule-row">
                  <div>
                    <p class="hr-text-muted hr-mb-1">Start Time</p>
                    <h5 class="hr-mb-0">{{ formatScheduleTime(clockData.schedule.startTime) }}</h5>
                  </div>
                  <div class="hr-text-end">
                    <p class="hr-text-muted hr-mb-1">End Time</p>
                    <h5 class="hr-mb-0">{{ formatScheduleTime(clockData.schedule.endTime) }}</h5>
                  </div>
                </div>
                <div class="hr-text-center">
                  <p class="hr-text-muted hr-mb-1">Break Duration</p>
                  <h5 class="hr-mb-0">{{ clockData.schedule.breakMinutes }} minutes</h5>
                </div>
              </div>
            </div>
            <div class="hr-chart-card">
              <div class="hr-chart-header">
                <h5 class="hr-chart-title"><i class="bx bx-bar-chart"></i> This Month</h5>
              </div>
              <div class="hr-chart-body">
                <div class="hr-stats-grid">
                  <div class="hr-stat-item">
                    <p class="hr-text-muted hr-mb-1 small">Days Present</p>
                    <h4 class="hr-mb-0 hr-text-success">{{ clockData.monthStats.totalDays }}</h4>
                  </div>
                  <div class="hr-stat-item">
                    <p class="hr-text-muted hr-mb-1 small">Total Hours</p>
                    <h4 class="hr-mb-0 hr-text-primary">{{ clockData.monthStats.totalHours }}h</h4>
                  </div>
                  <div class="hr-stat-item">
                    <p class="hr-text-muted hr-mb-1 small">Late Days</p>
                    <h4 class="hr-mb-0 hr-text-warning">{{ clockData.monthStats.lateDays }}</h4>
                  </div>
                  <div class="hr-stat-item">
                    <p class="hr-text-muted hr-mb-1 small">Overtime</p>
                    <h4 class="hr-mb-0 hr-text-info">{{ clockData.monthStats.totalOvertime }}h</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Today's Activity Log -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-history"></i> Today's Activity Log</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="clockData.todayLogs.length === 0" class="hr-empty-state">
              <i class="bx bx-time hr-empty-icon"></i>
              <p>No activity recorded today</p>
            </div>
            <div v-else class="timeline">
              <div v-for="(log, index) in clockData.todayLogs" :key="index" class="timeline-item">
                <div class="timeline-badge" :class="log.action === 'IN' || log.action === 'ENTER' ? 'hr-badge-success' : 'hr-badge-danger'">
                  <i :class="log.action === 'IN' || log.action === 'ENTER' ? 'bx bx-log-in' : 'bx bx-log-out'"></i>
                </div>
                <div class="timeline-panel">
                  <h6 class="timeline-title">
                    {{ log.action === 'IN' || log.action === 'ENTER' ? 'Clocked In' : 'Clocked Out' }}
                    <span v-if="log.location_type" class="hr-ml-2">
                      <span v-if="log.location_type === 'Office'" class="hr-badge hr-badge-success"><i class="bx bx-buildings"></i> Office</span>
                      <span v-else-if="log.location_type === 'Remote'" class="hr-badge hr-badge-info"><i class="bx bx-home"></i> Remote</span>
                      <span v-else class="hr-badge hr-badge-warning"><i class="bx bx-map"></i> Outside</span>
                    </span>
                  </h6>
                  <div class="timeline-body">
                    <p class="hr-text-muted hr-mb-1"><i class="bx bx-time"></i> {{ formatTime(log.timestamp) }}</p>
                    <p v-if="log.location" class="hr-text-muted hr-mb-1"><i class="bx bx-map-pin"></i> {{ log.location }}</p>
                    <p v-if="log.location_name && log.location_type === 'Office'" class="hr-text-muted hr-mb-1"><i class="bx bx-buildings"></i> <strong>{{ log.location_name }}</strong></p>
                    <p v-if="log.distance_from_office" class="hr-text-muted hr-mb-1"><i class="bx bx-map"></i> {{ formatDistance(log.distance_from_office) }} from office</p>
                    <p v-if="log.notes" class="hr-text-muted hr-mb-0"><i class="bx bx-note"></i> {{ log.notes }}</p>
                    <div v-if="log.latitude && log.longitude" class="hr-mt-2">
                      <small class="hr-text-muted"><i class="bx bx-current-location"></i> {{ log.latitude }}, {{ log.longitude }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Last 7 Days -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-header hr-flex-between">
            <h5 class="hr-chart-title"><i class="bx bx-calendar-week"></i> Last 7 Days</h5>
            <button @click="$router.push('/employee-attendance-records')" class="hr-btn hr-btn-sm hr-btn-outline-primary">
              <i class="bx bx-right-arrow-alt"></i> View All
            </button>
          </div>
          <div class="hr-chart-body">
            <div v-if="clockData.recentAttendance.length === 0" class="hr-text-muted hr-text-center hr-py-4">No recent attendance records</div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table">
                <thead><tr><th>Date</th><th>Check In</th><th>Check Out</th><th class="hr-text-center">Hours</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="record in clockData.recentAttendance" :key="record.id">
                    <td><strong>{{ formatDate(record.date) }}</strong></td>
                    <td>
                      <span v-if="record.first_in" class="hr-text-success"><i class="bx bx-log-in"></i> {{ record.first_in }}</span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="record.last_out" class="hr-text-danger"><i class="bx bx-log-out"></i> {{ record.last_out }}</span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td class="hr-text-center"><span class="hr-badge hr-badge-primary">{{ record.total_hours || '0' }}h</span></td>
                    <td><span :class="'hr-badge ' + getStatusBadgeClass(record.status)">{{ record.status }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Remote Attendance Requests -->
        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <h5 class="hr-chart-title"><i class="bx bx-home-alt"></i> Remote Attendance Requests</h5>
            <button @click="loadRemoteRequests" class="hr-btn hr-btn-sm hr-btn-outline-primary"><i class="bx bx-refresh"></i> Refresh</button>
          </div>
          <div class="hr-chart-body">
            <div v-if="remoteRequests.length === 0" class="hr-empty-state">
              <i class="bx bx-clipboard hr-empty-icon"></i>
              <p>No remote attendance submissions found.</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table">
                <thead><tr><th>Date</th><th>Action</th><th>Location</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="request in remoteRequests" :key="request.id">
                    <td><strong>{{ formatDate(request.accd) }}</strong><div class="hr-text-muted small">{{ formatTime(request.acct) }}</div></td>
                    <td><span class="hr-badge hr-badge-muted">{{ request.action === 'IN' ? 'Clock In' : 'Clock Out' }}</span></td>
                    <td><small class="hr-text-muted">{{ request.location_address || '—' }}</small></td>
                    <td><span :class="'hr-badge ' + getStatusBadgeClass(request.status)">{{ request.status }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Clock Action Modal -->
    <ModalWrapper ref="clockActionModal" :title="clockAction === 'IN' ? 'Clock In' : 'Clock Out'" :close-on-overlay="true">
      <template #body>
        <div class="hr-text-center hr-mb-4">
          <div class="hr-modal-icon" :class="clockAction === 'IN' ? 'hr-icon-success' : 'hr-icon-danger'">
            <i :class="clockAction === 'IN' ? 'bx bx-log-in' : 'bx bx-log-out'"></i>
          </div>
          <h5>{{ clockAction === 'IN' ? 'Ready to start your day?' : 'Finished for the day?' }}</h5>
          <p class="hr-text-muted">{{ currentTime }}</p>
        </div>
        <div class="hr-mb-3">
          <label class="hr-form-label">Location <span class="hr-text-danger">*</span></label>
          <select v-model="clockForm.locationId" @change="onClockLocationChange" class="hr-input">
            <option value="">Use current GPS location</option>
            <option v-for="location in clockData.workLocations" :key="location.id" :value="String(location.id)">
              {{ location.location_name }} - {{ location.address || location.location_type }}
            </option>
          </select>
          <p class="hr-text-muted small">Select a known location configured by your administrator, or leave empty to use your current GPS location.</p>

          <div v-if="gettingLocation" class="hr-alert hr-alert-info">
            <div class="hr-flex-align">
              <div class="hr-spinner small"></div>
              <span>Getting your location...</span>
            </div>
          </div>
          <div v-else-if="locationError" class="hr-alert hr-alert-danger">
            <i class="bx bx-error-circle"></i> {{ locationError }}
            <button @click="getCurrentLocation" class="hr-btn hr-btn-danger hr-mt-2 hr-w-100"><i class="bx bx-refresh"></i> Try Again</button>
          </div>
          <div v-else-if="clockForm.address" class="hr-alert hr-alert-success">
            <div class="hr-flex-between">
              <div>
                <i class="bx bx-map-pin"></i> <strong>Location Captured</strong>
                <p class="hr-mb-1 hr-mt-2">{{ clockForm.address }}</p>
                <small class="hr-text-muted">Coordinates: {{ clockForm.latitude }}, {{ clockForm.longitude }}</small>
              </div>
              <button v-if="!clockForm.locationId" @click="getCurrentLocation" class="hr-btn hr-btn-sm hr-btn-outline-success"><i class="bx bx-refresh"></i></button>
            </div>
          </div>
        </div>
        <div class="hr-mb-3">
          <label class="hr-form-label">Notes (Optional)</label>
          <textarea v-model="clockForm.notes" class="hr-textarea" rows="3" placeholder="Any notes about your shift..."></textarea>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.clockActionModal?.close()">Cancel</button>
        <button type="button" @click="confirmClockAction" class="hr-btn" :class="clockAction === 'IN' ? 'hr-btn-success' : 'hr-btn-danger'"
                :disabled="processing || gettingLocation || !clockForm.address">
          <span v-if="processing"><span class="hr-spinner small hr-me-1"></span>Processing...</span>
          <span v-else><i :class="clockAction === 'IN' ? 'bx bx-log-in' : 'bx bx-log-out'"></i> Confirm {{ clockAction === 'IN' ? 'Clock In' : 'Clock Out' }}</span>
        </button>
      </template>
    </ModalWrapper>

    <!-- Remote Attendance Modal -->
    <ModalWrapper ref="remoteAttendanceModal" title="Remote Attendance" :close-on-overlay="true">
      <template #body>
        <div class="hr-alert hr-alert-info hr-mb-3">
          <i class="bx bx-info-circle"></i>
          Use this option when you cannot clock in normally but are working remotely. Your manager will review and approve.
        </div>
        <div class="hr-mb-3">
          <label class="hr-form-label">Location / Address (optional)</label>
          <input type="text" v-model="remoteForm.address" class="hr-input" placeholder="e.g. Working from home, City, Country">
        </div>
        <div class="hr-mb-3">
          <label class="hr-form-label">Notes (optional)</label>
          <textarea v-model="remoteForm.notes" class="hr-textarea" rows="3" placeholder="Add any context for your manager"></textarea>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.remoteAttendanceModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-info" :disabled="remoteProcessing" @click="submitRemoteAttendance">
          <span v-if="remoteProcessing"><span class="hr-spinner small hr-me-1"></span>Submitting...</span>
          <span v-else>Submit For Approval</span>
        </button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeAttendanceClock",
  components: { HrPageLayout, ModalWrapper },

  data() {
    return {
      loading: false,
      processing: false,
      gettingLocation: false,
      locationError: null,
      clockData: null,
      currentTime: '',
      currentDate: '',
      clockAction: 'IN',
      clockForm: { latitude: null, longitude: null, address: '', notes: '', locationId: '' },
      timeInterval: null,
      workingDurationInterval: null,
      workingDuration: '00:00:00',
      remoteRequests: [],
      remoteProcessing: false,
      remoteForm: { notes: '', address: '' }
    };
  },

  mounted() {
    this.loadClockStatus();
    this.startClock();
    this.loadRemoteRequests();
  },

  beforeUnmount() {
    if (this.timeInterval) clearInterval(this.timeInterval);
    if (this.workingDurationInterval) clearInterval(this.workingDurationInterval);
  },

  methods: {
    async loadClockStatus() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeClockStatus");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.clockData = res.data.data;
          this.clockData.workLocations = this.clockData.workLocations || [];
          this.currentTime = res.data.data.currentTime;
          this.currentDate = res.data.data.currentDate;
          if (this.clockData.clockStatus.isClockedIn) this.startWorkingDurationCounter();
        } else {
          throw new Error(res.data?.message || 'Failed to load clock status');
        }
      } catch (error) {
        this.$toast?.error("Failed to load clock status: " + error.message) || console.error("Failed to load clock status:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadRemoteRequests() {
      try {
        const data = new FormData();
        data.append("function", "getMyRemoteAttendanceRequests");
        data.append("limit", 5);
        const res = await execute(data, authUrl);
        this.remoteRequests = (res && res.data && res.data.success) ? (res.data.data.requests || []) : [];
      } catch (error) {
        console.error("Failed to load remote attendance requests:", error);
        this.remoteRequests = [];
      }
    },
    startClock() {
      this.timeInterval = setInterval(() => {
        const now = new Date();
        this.currentTime = now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }, 1000);
    },
    startWorkingDurationCounter() {
      if (this.workingDurationInterval) clearInterval(this.workingDurationInterval);
      this.updateWorkingDuration();
      this.workingDurationInterval = setInterval(() => this.updateWorkingDuration(), 1000);
    },
    updateWorkingDuration() {
      if (!this.clockData || !this.clockData.clockStatus.checkInTime) return;
      const checkIn = new Date(this.clockData.clockStatus.checkInTime);
      const diff = new Date() - checkIn;
      const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
      this.workingDuration = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    openClockModal(action) {
      this.clockAction = action;
      this.clockForm = { latitude: null, longitude: null, address: '', notes: '', locationId: '' };
      this.locationError = null;
      this.$nextTick(() => this.$refs.clockActionModal?.open());
      this.getCurrentLocation();
    },
    onClockLocationChange() {
      if (!this.clockForm.locationId) {
        this.clockForm.latitude = null;
        this.clockForm.longitude = null;
        this.clockForm.address = '';
        this.locationError = null;
        this.getCurrentLocation();
        return;
      }

      const selected = this.clockData?.workLocations?.find(
        (location) => String(location.id) === String(this.clockForm.locationId)
      );
      if (selected) {
        this.clockForm.latitude = selected.latitude;
        this.clockForm.longitude = selected.longitude;
        this.clockForm.address = selected.address || `${parseFloat(selected.latitude).toFixed(6)}, ${parseFloat(selected.longitude).toFixed(6)}`;
        this.locationError = null;
        this.gettingLocation = false;
      }
    },
    async getCurrentLocation() {
      this.gettingLocation = true;
      this.locationError = null;
      if (!navigator.geolocation) {
        this.locationError = 'Geolocation is not supported by your browser';
        this.gettingLocation = false;
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          this.clockForm.latitude = pos.coords.latitude;
          this.clockForm.longitude = pos.coords.longitude;
          await this.getAddressFromCoordinates(pos.coords.latitude, pos.coords.longitude);
          this.gettingLocation = false;
        },
        (err) => {
          this.gettingLocation = false;
          if (err.code === 1) this.locationError = 'Location access denied. Please enable location permissions in your browser settings.';
          else if (err.code === 2) this.locationError = 'Location information is unavailable.';
          else if (err.code === 3) this.locationError = 'Location request timed out. Please try again.';
          else this.locationError = 'An unknown error occurred while getting location.';
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    },
    async getAddressFromCoordinates(lat, lng) {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, { headers: { 'Accept': 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          const a = data.address;
          const parts = [a.road, a.suburb, a.city, (a.county && !a.city) ? a.county : null, a.country].filter(Boolean);
          this.clockForm.address = parts.length ? parts.join(', ') : data.display_name;
        } else {
          this.clockForm.address = `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        }
      } catch (e) {
        this.clockForm.address = `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    },
    async confirmClockAction() {
      if (!this.clockForm.latitude || !this.clockForm.longitude || !this.clockForm.address) {
        this.$toast?.error("Location is required. Please allow location access or select a valid location.") || alert("Location is required. Please allow location access or select a valid location.");
        return;
      }
      this.processing = true;
      try {
        const data = new FormData();
        data.append("function", "employeeClockAction");
        data.append("action", this.clockAction);
        data.append("latitude", this.clockForm.latitude);
        data.append("longitude", this.clockForm.longitude);
        data.append("address", this.clockForm.address);
        data.append("notes", this.clockForm.notes);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message) || alert(res.data.message);
          this.$refs.clockActionModal?.close();
          await this.loadClockStatus();
        } else throw new Error(res.data?.message || 'Failed to record attendance');
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.processing = false;
      }
    },
    openRemoteModal() {
      this.remoteForm = { notes: '', address: '' };
      this.$nextTick(() => this.$refs.remoteAttendanceModal?.open());
    },
    async submitRemoteAttendance() {
      if (this.remoteProcessing) return;
      this.remoteProcessing = true;
      try {
        const data = new FormData();
        data.append("function", "markRemoteAttendanceManual");
        if (this.remoteForm.notes) data.append("notes", this.remoteForm.notes);
        if (this.remoteForm.address) data.append("address", this.remoteForm.address);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Remote attendance submitted") || alert(res.data.message || "Remote attendance submitted");
          this.$refs.remoteAttendanceModal?.close();
          await this.loadClockStatus();
          await this.loadRemoteRequests();
        } else throw new Error(res?.data?.message || "Failed to submit remote attendance");
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.remoteProcessing = false;
      }
    },
    formatDistance(m) {
      if (!m) return 'N/A';
      return m < 1000 ? Math.round(m) + 'm' : (m / 1000).toFixed(1) + 'km';
    },
    formatTime(dt) {
      if (!dt) return 'N/A';
      return new Date(dt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    formatScheduleTime(t) {
      if (!t) return 'N/A';
      const [h, m] = t.split(':');
      const d = new Date();
      d.setHours(parseInt(h), parseInt(m));
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    formatDate(d) {
      if (!d) return 'N/A';
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    formatDateLong(d) {
      if (!d) return 'N/A';
      return new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    },
    getStatusBadgeClass(status) {
      const s = status?.toLowerCase();
      if (s === 'pending') return 'hr-badge-warning';
      if (s === 'approved' || s === 'present') return 'hr-badge-success';
      if (s === 'rejected' || s === 'absent') return 'hr-badge-danger';
      if (s === 'late') return 'hr-badge-warning';
      if (s === 'partial') return 'hr-badge-secondary';
      if (s === 'overtime') return 'hr-badge-info';
      return 'hr-badge-secondary';
    }
  }
};
</script>

<style src="@/assets/css/hr-page-shared.css"></style>
<style scoped>
.hr-clock-hero {
  background: linear-gradient(135deg, #5b73e8 0%, #3b5bdb 100%);
  color: white;
  text-align: center;
  padding: 2.5rem;
  border-radius: 20px;
}
.hr-clock-time { font-size: 4rem; font-weight: 800; margin: 0; color: white; }
.hr-clock-date { color: rgba(255,255,255,0.8); margin: 0.5rem 0 1rem; }
.hr-clock-hero .hr-badge-muted { background: rgba(255,255,255,0.2); color: white; }
.hr-clock-status-icon { width: 80px; height: 80px; margin: 0 auto 1rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 40px; }
.hr-status-in { background: #d1fae5; color: #047857; }
.hr-status-out { background: #fee2e2; color: #b91c1c; }
.hr-clock-actions { margin-top: 1.5rem; }
.hr-btn-lg { padding: 12px 24px; font-size: 16px; }
.hr-w-100 { width: 100%; }
.hr-schedule-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; }
.hr-text-end { text-align: right; }
.hr-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; text-align: center; }
.hr-stat-item { padding: 8px 0; }
.hr-modal-icon { width: 64px; height: 64px; margin: 0 auto 1rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 32px; }
.hr-icon-success { background: #d1fae5; color: #047857; }
.hr-icon-danger { background: #fee2e2; color: #b91c1c; }
.hr-flex-align { display: flex; align-items: center; gap: 8px; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.hr-flex-between-wrap { flex-wrap: wrap; }
.hr-spinner.small { width: 20px; height: 20px; border-width: 2px; }
.hr-me-1 { margin-right: 4px; }
.hr-ml-2 { margin-left: 8px; }
.timeline { list-style: none; padding: 20px 0; position: relative; }
.timeline:before { content: ''; position: absolute; top: 0; bottom: 0; width: 2px; background: #e2e8f0; left: 30px; }
.timeline-item { margin-bottom: 20px; position: relative; padding-left: 60px; }
.timeline-badge { width: 40px; height: 40px; border-radius: 50%; position: absolute; left: 10px; top: 0; display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; border: 3px solid white; box-shadow: 0 0 0 2px #e2e8f0; }
.timeline-badge.hr-badge-success { background: #10b981; }
.timeline-badge.hr-badge-danger { background: #ef4444; }
.timeline-panel { background: #f8fafc; padding: 15px; border-radius: 12px; border-left: 3px solid #5b73e8; }
.timeline-title { margin: 0; font-size: 16px; font-weight: 600; }
.timeline-body { margin-top: 8px; }
.hr-empty-state { text-align: center; padding: 2rem; color: #94a3b8; }
.hr-empty-icon { font-size: 48px; display: block; margin-bottom: 0.5rem; }
.hr-loading-wrap { text-align: center; padding: 3rem; }
.hr-spinner { width: 40px; height: 40px; border: 3px solid rgba(91,115,232,0.2); border-top-color: #5b73e8; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1rem; }
@keyframes spin { to { transform: rotate(360deg); } }
.hr-alert { padding: 12px 16px; border-radius: 8px; }
.hr-alert-info { background: rgba(23,162,184,0.1); color: #0c5460; }
.hr-alert-danger { background: rgba(220,53,69,0.1); color: #721c24; }
.hr-alert-success { background: rgba(40,167,69,0.1); color: #155724; }
.hr-text-primary { color: #5b73e8; }
.hr-text-warning { color: #f59e0b; }
.hr-text-info { color: #06b6d4; }
</style>
